import * as React from 'react';
import { connect } from 'react-redux';
import { Card } from 'react-bootstrap';
import { ResponsiveBullet } from '@nivo/bullet';
import { BasicTooltip } from '@nivo/tooltip';
import { Md5 } from 'ts-md5';

/**
 * Props
 *
 */
type Props = {
  tempoEsperaParticular: DataTempoEspera,
  tempoEsperaConvenio: DataTempoEspera,
};

/**
 * State
 *
 */
type State = {
  dataChartTempoEspera: Array<DataChartTempoAtendimeto>,
  hashProps: string,
  hasData: boolean,
};

/**
 * Data interface
 *
 */
interface DataTempoEspera {
  minimo: number;
  medio: number;
  maximo: number;
}

/**
 * Date interface for chart time
 *
 */
interface DataChartTempoAtendimeto {
  id: string;
  ranges: Array<number>;
  measures: Array<number>;
  markers: Array<number>;
}

/**
 * Fixed ranges for time
 *
 * @type {number[]}
 */
const ranges = [0, 60, 120];

/**
 * Custom tooltip for chart tempo de atendimento
 *
 * @param v0 - v0
 * @param v1 - v1
 * @param color - color
 * @returns {JSX.Element}
 * @constructor
 */
const CustomTooltip = ({
  v0,
  v1,
  color,
}: {
  color: string,
  v0: number,
  v1?: number,
}) => {
  return (
    <BasicTooltip
      id={
        v1 ? (
          <span>
            <strong>{v0}</strong> a <strong>{v1}</strong>
          </span>
        ) : (
          <strong>{v0}</strong>
        )
      }
      enableChip={true}
      color={color}
    />
  );
};

/**
 * Chart Dashboard Component
 *
 */
class ChartTempoEsperaDashboardComponent extends React.Component<Props, State> {
  /**
   * Default constructor
   *
   * @param props
   */
  constructor(props: any) {
    super(props);
    this.state = this.getInitialState();
  }

  /**
   * Initial state
   *
   * @return {}
   */
  getInitialState() {
    return {
      dataChartTempoEspera: [
        {
          id: 'Particular',
          ranges: ranges,
          measures: [0],
          markers: [
            this.props.tempoEsperaParticular.minimo,
            this.props.tempoEsperaParticular.medio,
            this.props.tempoEsperaParticular.maximo,
          ],
        },
        {
          id: 'Convênio',
          ranges: ranges,
          measures: [0],
          markers: [
            this.props.tempoEsperaConvenio.minimo,
            this.props.tempoEsperaConvenio.medio,
            this.props.tempoEsperaConvenio.maximo,
          ],
        },
      ],
      hashProps: this.generateHasProps(
        this.props.tempoEsperaParticular,
        this.props.tempoEsperaConvenio
      ),
      hasData: this.hasData(),
    };
  }

  /**
   * Responsible change componente.
   *
   * @param prevProps
   * @param prevState
   * @param snapshot
   */
  componentDidUpdate(
    prevProps: Readonly<any>,
    prevState: Readonly<any>,
    snapshot?: any
  ): void {
    let newHashProps: string = this.generateHasProps(
      this.props.tempoEsperaParticular,
      this.props.tempoEsperaConvenio
    );
    if (newHashProps !== prevState.hashProps) {
      this.setState(this.getInitialState());
    }
  }

  /**
   * Responsible generate hash.
   *
   * @param tempoEsperaParticular
   * @param tempoEsperaConvenio
   * @return {string}
   */
  generateHasProps = (
    tempoEsperaParticular: DataTempoEspera,
    tempoEsperaConvenio: DataTempoEspera
  ): string => {
    return Md5.hashStr(
      JSON.stringify(tempoEsperaParticular) +
        JSON.stringify(tempoEsperaConvenio)
    );
  };

  /**
   *
   * Responsible validate if has data.
   *
   * @return {boolean}
   */
  hasData = (): boolean => {
    return (
      this.props.tempoEsperaParticular &&
      this.props.tempoEsperaConvenio &&
      !isNaN(this.props.tempoEsperaParticular.minimo) &&
      !isNaN(this.props.tempoEsperaParticular.medio) &&
      !isNaN(this.props.tempoEsperaParticular.maximo) &&
      !isNaN(this.props.tempoEsperaConvenio.minimo) &&
      !isNaN(this.props.tempoEsperaConvenio.medio) &&
      !isNaN(this.props.tempoEsperaConvenio.maximo) &&
      (this.props.tempoEsperaParticular.minimo >= 0 ||
        this.props.tempoEsperaParticular.medio > 0 ||
        this.props.tempoEsperaParticular.maximo > 0 ||
        this.props.tempoEsperaConvenio.minimo >= 0 ||
        this.props.tempoEsperaConvenio.medio > 0 ||
        this.props.tempoEsperaConvenio.maximo > 0)
    );
  };

  /**
   * Render component
   *
   * @returns {JSX.Element}
   */
  render() {
    return (
      <React.Fragment>
        <Card className="chart-card bg-light pr-0 pl-0">
          <Card.Body className="chart-card-body pr-0 pl-0">
            <Card.Title className="chart-card-title d-flex justify-content-center">
              Tempo de Espera
            </Card.Title>
            {!this.state.hasData && (
              <React.Fragment>
                <div className="chart-text-blank">
                  Não existem dados a serem exibidos para essa pesquisa.
                </div>
              </React.Fragment>
            )}
            {this.state.hasData && (
              <React.Fragment>
                <div className="chart-card-h85p">
                  <ResponsiveBullet
                    data={this.state.dataChartTempoEspera}
                    layout="vertical"
                    spacing={80}
                    margin={{ right: 70, left: 50, top: 50, bottom: 15 }}
                    titleAlign="start"
                    titleOffsetX={0}
                    titleOffsetY={-15}
                    titleRotation={-40}
                    measureSize={0.15}
                    measureBorderWidth={0}
                    rangeColors={['#8ea2c0', '#013A66FF']}
                    markerColors={['#7c8657', '#b95722', '#831a26']}
                    markerSize={0.8}
                    tooltip={CustomTooltip}
                    animate={true}
                  />
                </div>
                <div className="chart-tempo-atendimento-lengenda-main d-flex justify-content-center">
                  <hr className="chart-tempo-atendimento-legenda chart-tempo-atendimento-minimo" />
                  <span>Minimo</span>
                  <hr className="chart-tempo-atendimento-legenda chart-tempo-atendimento-medio" />
                  <span>Médio</span>
                  <hr className="chart-tempo-atendimento-legenda chart-tempo-atendimento-maximo" />
                  <span>Máximo</span>
                </div>
              </React.Fragment>
            )}
          </Card.Body>
        </Card>
      </React.Fragment>
    );
  }
}

/**
 * Export default connect
 *
 */
export default connect(null, null, null)(ChartTempoEsperaDashboardComponent);
